<template>
    <div v-if="shouldRender"
         :class="wrapperClasses">

        <a v-if="isLink"
           ref="control"
           :id="id || undefined"
           :href="hrefValue"
           :target="target || undefined"
           :rel="target === '_blank' ? 'noopener noreferrer' : undefined"
           :tabindex="tabIndex"
           :aria-label="computedAriaLabel"
           :aria-expanded="ariaExpanded"
           :aria-hidden="ariaHidden || undefined"
           :class="buttonClasses"
           :data-testid="`button-${variant.toLowerCase() || 'primary'}`"
           data-cursor="link"
           @click="handleClick"
           @blur="$emit('blur', $event)"
           @focus="$emit('focus', $event)"
           @mouseenter="handleMouseEnter">

            <Icon v-if="leftIcon"
                  class-name="icon leftIcon"
                  :name="leftIcon"
                  hidden />

            <span v-if="variant === 'Text' && text"
                  class="text">
                {{ text }}
            </span>

            <span v-else-if="fullWidth && text"
                  class="text">
                {{ text }}
            </span>

            <span v-else-if="text"
                  class="text motionTrackSlide"
                  :data-animate="triggerAnimation">
                {{ text }}
            </span>

            <Icon v-if="rightIcon"
                  class-name="icon rightIcon"
                  :name="rightIcon"
                  hidden />

        </a>

        <button v-else
                ref="control"
                :id="id || undefined"
                :type="type"
                :disabled="disabled"
                :tabindex="tabIndex"
                :aria-label="computedAriaLabel"
                :aria-expanded="ariaExpanded"
                :aria-hidden="ariaHidden || undefined"
                :class="buttonClasses"
                :data-testid="`button-${variant.toLowerCase() || 'primary'}`"
                data-cursor="link"
                @click="handleClick"
                @blur="$emit('blur', $event)"
                @focus="$emit('focus', $event)"
                @mouseenter="handleMouseEnter">

            <Icon v-if="leftIcon"
                  class-name="icon leftIcon"
                  :name="leftIcon"
                  hidden />

            <span v-if="variant === 'Text' && text"
                  class="text">
                {{ text }}
            </span>

            <span v-else-if="fullWidth && text"
                  class="text">
                {{ text }}
            </span>

            <span v-else-if="text"
                  class="text motionTrackSlide"
                  :data-animate="triggerAnimation">
                {{ text }}
            </span>

            <Icon v-if="rightIcon"
                  class-name="icon rightIcon"
                  :name="rightIcon"
                  hidden />

        </button>

    </div>
</template>

<script>
    import Icon from '@/components/Icon.vue';

    export default {
        
        name: 'Button',
        components: {
            Icon
        },
        props: {
            ariaExpanded: {
                type: [Boolean, String],
                default: undefined,
            },
            ariaLabel: {
                type: String,
                default: '',
            },
            ariaHidden: {
                type: Boolean,
                default: false,
            },
            className: {
                type: String,
                default: '',
            },
            disabled: {
                type: Boolean,
                default: false,
            },
            doUnderline: {
                type: Boolean,
                default: false,
            },
            fullWidth: {
                type: Boolean,
                default: false,
            },
            id: {
                type: String,
                default: '',
            },
            innerClassName: {
                type: String,
                default: '',
            },
            leftIcon: {
                type: String,
                default: '',
            },
            rightIcon: {
                type: String,
                default: '',
            },
            href: {
                type: String,
                default: '',
            },
            link: {
                type: Object,
                default: null,
            },
            noBackground: {
                type: Boolean,
                default: false,
            },
            size: {
                type: String,
                default: 'medium',
                validator: value => ['large', 'medium', 'small'].includes(value),
            },
            tabIndex: {
                type: [Number, String],
                default: undefined,
            },
            target: {
                type: String,
                default: '',
            },
            text: {
                type: String,
                default: '',
            },
            type: {
                type: String,
                default: 'button',
            },
            variant: {
                type: String,
                default: 'Primary',
                validator: value => ['Primary', 'Secondary', 'Tertiary', 'Text'].includes(value),
            },
        },

        emits: ['click', 'blur', 'focus'],

        data() {
            return {
                triggerAnimation: false,
            };
        },

        computed: {
            shouldRender() {
                return Boolean(this.leftIcon || this.rightIcon || this.text);
            },

            isLink() {
                return Boolean(this.href || this.linkHref);
            },

            linkHref() {
                if (!this.link) {
                    return '';
                }

                return this.link.href ||
                    this.link.url ||
                    this.link.externalLink ||
                    this.link.internalLink ||
                    '';
            },

            hrefValue() {
                return this.href || this.linkHref;
            },

            tagName() {
                return this.isLink ? 'a' : 'button';
            },

            computedAriaLabel() {
                if (this.ariaHidden) {
                    return undefined;
                }

                return this.ariaLabel || undefined;
            },

            wrapperClasses() {
                return [
                    this.className,
                    'buttonComponentWrapper',
                    {
                        isDisabledWrapper: this.disabled,
                        isFullWidth: this.fullWidth,
                    },
                ];
            },

            buttonClasses() {
                return [
                    this.innerClassName,
                    'button-vue-component',
                    {
                        isLarge: this.size === 'large',
                        isMedium: this.size === 'medium',
                        isSmall: this.size === 'small',

                        isPrimary: this.variant === 'Primary',
                        isSecondary: this.variant === 'Secondary',
                        isTertiary: this.variant === 'Tertiary',
                        isText: this.variant === 'Text',

                        isDisabled: this.disabled,
                        isOnlyIcon: !this.text && (this.leftIcon || this.rightIcon),
                        doUnderline: this.doUnderline,
                        noBackground: this.noBackground,
                    },
                ];
            },
        },

        methods: {
            handleClick(event) {
                if (this.disabled) {
                    event.preventDefault();
                    return;
                }

                this.$emit('click', event);
            },

            handleMouseEnter() {
                if (this.variant !== 'Text' && !this.fullWidth) {
                    this.triggerAnimation = !this.triggerAnimation;
                }
            },

            focus() {
                this.$refs.control?.focus();
            },
        },
       
    };
</script>